import { createSelector } from 'reselect';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchTutorials } from '../api/tutorials';
import { createReducer } from '../reducers/factory';
import { TUTORIALS_PLACEMENT } from '../constants';
import { userSelector } from '../selectors';

const NOT_LOGGEDIN_TUTORIAL_READ = 'NOT_LOGGEDIN_TUTORIAL_READ';
const CLEAR_TUTORIALS_READ = 'CLEAR_TUTORIALS_READ';

const fetchReducer = createReducer('TUTORIALS', {
  fetchTimeout: 30 * 1000,
  invalidateActions: ['LOGIN_SUCCESSFUL', 'SET_LANGUAGE'],
  minUpdate: 30 * 60 * 1000,
  initialState: {
    items: [],
    tutorialsRead: {},
  },
});

const name = 'tutorials';

export const tutorialPlainSelector = ({ [name]: tutorials }) => tutorials;

export const tutorialsReadOfflineSelector = createSelector(
  tutorialPlainSelector,
  ({ tutorialsRead }) => tutorialsRead,
);
export const tutorialsSelector = createSelector(
  tutorialPlainSelector,
  ({
    fetchingInProgressSince,
    lastSuccessfulFetch,
    lastFailedFetch,
    ...rest
  }) => ({
    ...rest,
    loading:
      !!fetchingInProgressSince || (!lastSuccessfulFetch && !lastFailedFetch),
  }),
);

export const tutorialsReadByUserSelector = createSelector(
  userSelector,
  ({ tutorialsRead = [] }) => {
    const tutorialsReadMap = {};
    tutorialsRead.forEach((id) => {
      tutorialsReadMap[id] = true;
    });
    return tutorialsReadMap;
  },
);

export const tutorialsReadSelector = createSelector(
  tutorialsReadByUserSelector,
  tutorialsReadOfflineSelector,
  (userTutorials, offlineTutorials) => ({
    ...userTutorials,
    ...offlineTutorials,
  }),
);

const tutorialsSelectorFactory = (tutorialPlacement) =>
  createSelector(
    tutorialsReadSelector,
    tutorialsSelector,
    (tutorialsRead, { items, ...tutorials }) => ({
      items: items.filter(
        ({ placement, _id }) =>
          placement === tutorialPlacement && !tutorialsRead[_id],
      ),
      ...tutorials,
    }),
  );

export const tutorialsHomeSelector = tutorialsSelectorFactory(
  TUTORIALS_PLACEMENT.HOME,
);
export const tutorialsComicsSelector = tutorialsSelectorFactory(
  TUTORIALS_PLACEMENT.COMICS,
);
export const tutorialsDiamondsSelector = tutorialsSelectorFactory(
  TUTORIALS_PLACEMENT.DIAMONDS,
);
export const tutorialsInventorySelector = tutorialsSelectorFactory(
  TUTORIALS_PLACEMENT.INVENTORY,
);
export const tutorialsRadarSelector = tutorialsSelectorFactory(
  TUTORIALS_PLACEMENT.RADAR,
);

const fetchTutorialsAction =
  ({ platform, version } = {}) =>
  async (dispatch, getState) => {
    const {
      auth: { apiUrl },
    } = getState();

    if (!apiUrl) {
      return {};
    }

    const fetchPromise = (async () => {
      try {
        dispatch(
          fetchReducer.fetchSuccessful(
            await fetchTutorials(apiUrl, { platform, version }),
          ),
        );
      } catch (e) {
        dispatch(fetchReducer.fetchFailed(e));
      }
    })();

    dispatch(fetchReducer.fetchInProgress(fetchPromise));
    return fetchPromise;
  };

export const fetchTutorialsIfNeeded =
  ({ platform, version } = {}) =>
  async (dispatch, getState) => {
    const { fetchPromise, items, ...state } = tutorialPlainSelector(getState());

    if (fetchReducer.shouldFetch(state)) {
      return dispatch(fetchTutorialsAction({ platform, version }));
    }

    return typeof fetchPromise?.then === 'function' ? fetchPromise : items;
  };

export function saveReadTutorialId(_id) {
  return {
    type: NOT_LOGGEDIN_TUTORIAL_READ,
    _id,
  };
}

export const clearTutorialsRead = () => ({ type: CLEAR_TUTORIALS_READ });

export const useTutorial = (tutorialId) => {
  const { items, loading } = useSelector(tutorialsSelector);
  return useMemo(
    () => ({ ...(items.find(({ _id }) => _id === tutorialId) || {}), loading }),
    [items, loading, tutorialId],
  );
};

function TutorialsReducer(state = fetchReducer.INITIAL_STATE, action = {}) {
  switch (action.type) {
    case NOT_LOGGEDIN_TUTORIAL_READ:
      return {
        ...state,
        tutorialsRead: { ...state.tutorialsRead, [action._id]: true },
      };
    case CLEAR_TUTORIALS_READ:
      return {
        ...state,
        tutorialsRead: {},
      };
    default:
      return fetchReducer(state, action);
  }
}

export const createTutorialsHook =
  (languageSelector, platform, version) => (selector) => {
    const dispatch = useDispatch();
    const language = useSelector(languageSelector);

    useEffect(() => {
      dispatch(fetchTutorialsIfNeeded({ platform, version })).catch(() => {});
    }, [dispatch, language]);
    return useSelector(selector);
  };

export default { [name]: TutorialsReducer };
